import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import { useSelector } from 'react-redux'
import { v4 } from 'uuid'
import { RootState, useAppDispatch } from '../..'
import useNextPrevKeyboard from '../../hooks/useNextPrevKeyboard'
import { useQuery } from '../../hooks/useQuery'
import { useTopics } from '../../hooks/useTopics'
import { EmployeeDetails, EmployeeService } from '../../services/EmployeeService'
import {
  trackEvent,
  trackEventImmediately,
  TrackingCategoryEnum,
  TrackingCategoryEventEnum,
} from '../../services/EventTrackingService'
import { mapFormsToFormSelectOptions, sortFormCategories } from '../../utils/forms/FormUtils'
import { FormService } from '../../services/FormService'
import { AnswerToBeAddedToReview, ReviewService } from '../../services/ReviewService'
import { finishTutorial, hideTutorial, showHelpId } from '../../slices/help-slice'
import { AnswerT, getAnswerSkipReason, isAnswerSkipped } from '../../types/definitions/answer'
import { Form } from '../../types/definitions/form'
import { AnsweredQuestion, Question, QuestionTopic } from '../../types/definitions/question'
import { Review } from '../../types/definitions/review'
import { TalentRankSummary } from '../../types/definitions/talentRank'
import { AnswerSkipReasonEnum } from '../../types/enums/answer-skip-reason.enum'
import { QuestionTypeEnum } from '../../types/enums/question-type.enum'
import { createPathWithLang } from '../../utils/languagePathUtils'
import { NetworkConstants } from '../../utils/NetworkConstants'
import { fillAnswersRandomly, _sortQuestions } from '../../utils/questionUtils'
import { AppButton } from '../atoms/AppButton/AppButton'
import { AppSelect } from '../atoms/AppSelect/AppSelect'
import { ArrowIcon } from '../atoms/Icons/ArrowIcon'
import DeleteIcon from '../atoms/Icons/DeleteIcon.svg'
import GarbageSVG from '../atoms/Icons/Garbage.svg'
import WarningIcon from '../atoms/Icons/WarningIcon.svg'
import Spinner from '../atoms/Spinner'
import { CategorySelector } from '../molecules/CategorySelector/CategorySelector'
import ErrorModal from '../molecules/ErrorModal'
import { NavigationBlocker } from '../molecules/ExitBlocker/NavigationBlocker'
import HelpTooltip from '../molecules/HelpTooltip'
import OptionsModal from '../molecules/OptionsModal'
import { ChecklistQuestion } from '../molecules/Questions/ChecklistQuestion/ChecklistQuestion'
import { DropdownQuestion } from '../molecules/Questions/DropdownQuestion/DropdownQuestion'
import { SelectQuestion } from '../molecules/Questions/SelectQuestion/SelectQuestion'
import { SliderQuestion } from '../molecules/Questions/SliderQuestion/SliderQuestion'
import SliderTextQuestion from '../molecules/Questions/SliderTextQuestion/SliderTextQuestion'
import { TextareaQuestion } from '../molecules/Questions/TextareaQuestion/TextareaQuestion'
import { TutorialModal } from '../molecules/TutorialModal'
import EmployeeReviewFormSelectionStep from '../organisms/EmployeeReviewFormSelectionStep'
import { NextPrevArrows } from '../organisms/NextPrevArrows'
import { ConstantIDs } from '../../constants/employee'
import { iconsEmployee } from '../../utils/iconsUtils'
import { TrueFalseQuestion } from '../molecules/Questions/ChecklistQuestion/TrueFalseQuestion'
import { TalentRankRecommendation } from '../../utils/talentRankSuggestion'
import {
  calculateTalentRankSummary,
  DEFAULT_TALENT_RANK_FALLBACK_LABELS,
  findTalentRankQuestion,
  getScoredCategoryNames,
} from '../../utils/talentRankSummary'
import { isOverrideJustificationTitle } from '../../utils/overrideJustification'
import { isTalentRankTitle } from '../../utils/talentRankTitle'
import { FormCategory } from '../../types/definitions/formCategory'
import { getFormById } from '../../utils/forms/FormUtils'
import { shouldTreatAutoCalculatedScoreAsOptional } from '../../utils/reviewCompletion'
import classNames from 'classnames'

const REVIEW_FORM_MAIN_PATH = RegExp(/\/[a-z]+\/(employee-)?review\/[0-9]+.*/)
const EMAIL = 'mailto:grapes@theagilemonkeys.com'
const SLIDER_QUESTION_ID = v4()
const NAME_LENGTH = 25
const JOB_TITLE_LENGTH = 20

const getDraftFlowErrorDetails = (error: unknown): Record<string, unknown> => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    }
  }

  if (typeof error === 'object' && error !== null) {
    const typedError = error as {
      message?: string
      response?: {
        status?: number
        data?: unknown
      }
    }

    return {
      message: typedError.message,
      status: typedError.response?.status,
      data: typedError.response?.data,
    }
  }

  return {
    error,
  }
}

const trackDraftFlowEvent = async (event: string, details: Record<string, unknown>) => {
  try {
    await trackEventImmediately(TrackingCategoryEnum.ASSESSMENT, event, JSON.stringify(details))
  } catch (trackingError) {
    console.error('Failed to send draft flow tracking event', trackingError)
  }
}

const trackDraftFlowErrorEvent = async (details: Record<string, unknown>, error: unknown) => {
  await trackDraftFlowEvent(TrackingCategoryEventEnum.ASSESSMENT.SAVE_DRAFT_FLOW_ERROR, {
    ...details,
    ...getDraftFlowErrorDetails(error),
  })
}

const deleteModalMessage = (
  <p className={`modal-title`}>
    Are you sure you want to delete all the data from this review and restart the assessment?
  </p>
)

const exitWithoutChangesModalMessage = (
  <p>
    <span className={`modal-title`}> Are you sure you want to leave without saving?</span>
    <br />
    <b>All the data from this review will be lost.</b>
  </p>
)

const TutorialIntroduction = () => {
  return (
    <div className="container flex justify-center my-auto">
      <div className="flex flex-col justify-center">
        <div className="w-full text-center leading-[3rem]">
          <p className="text-dark-blue text-5xl font-medium mb-10 leading-[4.5rem]">
            <b>Complete a check-up of your team members' capabilities</b> in 5-10 minutes.
          </p>
          <div className="text-3xl font-normal text-dark-blue mb-12 flex flex-col gap-8">
            <p>
              The resulting data will help you <b>evaluate, understand and coach your team better</b>.
            </p>
            <p>
              Please note: <b>this is not a formal review</b>. We encourage you to update information periodically so
              you can monitor progress.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

const UserTutorialIntroduction = () => {
  return (
    <div className="container flex justify-center my-auto">
      <div className="flex flex-col justify-center">
        <div className="w-full text-center leading-[3rem]">
          <p className="text-6xl font-medium mb-16">Welcome to Self-assessments</p>
          <div className="text-3xl font-normal mb-12 flex flex-col gap-8">
            <p>
              In 10-15 minutes you can complete a <b>check-up of your current professional profile</b>.
            </p>
            <p>
              The resulting data will help you and your manager <b>work on your professional development plan</b> and{' '}
              <b>achieve growth goals</b>.
            </p>
            <p>
              Please note: <b>this is not a formal review</b>. We encourage you to update the information periodically
              so you can monitor progress.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

interface SliderValues {
  [key: string]: {
    value: number
    hasUserAnswer: boolean
  }
}

const checkIfExistUnsavedAnswers = (indexedAnswers: Record<string, AnswerT>, currentReview?: Review) => {
  const indexedSavedAnswers: Record<string, AnsweredQuestion> = {}

  ;(currentReview?.answers || []).forEach(savedAnswer => {
    indexedSavedAnswers[savedAnswer.id] = savedAnswer
  })

  return Object.entries(indexedAnswers).some(([answerId, answer]) => {
    return !(
      answer.answer === undefined ||
      JSON.stringify(indexedSavedAnswers[answerId]?.answer) === JSON.stringify(answer?.answer)
    )
  })
}

export const EmployeeReview = (): JSX.Element => {
  useEffect(() => {
    dispatch(hideTutorial())
    trackEvent(TrackingCategoryEnum.ASSESSMENT, TrackingCategoryEventEnum.ASSESSMENT.PAGE_ON_LOAD, '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  let fillAnswersClickCounter = 0
  const history = useHistory()
  const query = useQuery()
  const dispatch = useAppDispatch()
  const currentTopicToDisplay = query.get('topic')
  const user = useSelector<RootState, EmployeeDetails | null>(state => state.auth.user)
  const { employeeId, reviewId } = useParams<{ employeeId: string; reviewId: string }>()
  const [groupedQuestions, setGroupedQuestions] = useState<Record<string, Question[]>>({})
  const [indexedQuestions, setIndexedQuestions] = useState<Record<string, Question>>({})
  const [topics, setTopics] = useState<QuestionTopic[]>([])
  const [indexedAnswers, setIndexedAnswers] = useState<Record<string, AnswerT>>({})
  const [reviewedEmployee, setReviewedEmployee] = useState<EmployeeDetails>()
  const [currentReview, setCurrentReview] = useState<Review>()
  const [loading, setLoading] = useState<boolean>(false)
  const [isUnfinishedTopic, setUnfinishedTopic] = useState<boolean>(false)
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false)
  const [sliderValues, setSliderValues] = useState<SliderValues>({})
  const [allForms, setAllForms] = useState<Form[]>()
  const [allFormCategories, setAllFormCategories] = useState<FormCategory[]>()
  const [currentForm, setCurrentForm] = useState<Form>()
  const [selectedFormId, setSelectedFormId] = useState<number>()
  const [showChangeFormModal, setShowChangeFormModal] = useState<boolean>()
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [lastFormUsed, setLastFormUsed] = useState<Form>()
  const [reviewsFromUser, setReviewsFromUser] = useState<Review[]>([])
  const [questionsPreviousAnswers, setQuestionsPreviousAnswers] = useState<Record<string, AnsweredQuestion>>()
  const [talentRankSuggestion, setTalentRankSuggestion] = useState<string>()

  const divContentQuestionsRef = useRef<HTMLDivElement>(null)
  const tutorialCompleted = useSelector<RootState, boolean>(state => state.help.completed)
  const userTutorialCompleted = useSelector<RootState, boolean>(state => state.help.userAssesmentCompleted)
  const [introductionView, setIntroductionView] = useState<boolean>(tutorialCompleted)
  const [userIntroductionView, setUserIntroductionView] = useState<boolean>(userTutorialCompleted)

  const isTutorialCompleted =
    (tutorialCompleted && user?.role !== 'user') || (userTutorialCompleted && user?.role === 'user')

  const {
    currentTopicIndex,
    currentTopic,
    handleBackToPreviousTopic,
    handleGoToNextTopic,
    isFirstTopic,
    isLastTopic,
    hasElementFocus,
    resetTopic,
  } = useTopics(topics, currentTopicToDisplay, divContentQuestionsRef)

  const isNextDisabled = hasElementFocus || isLastTopic
  const isPreviousDisabled = hasElementFocus || isFirstTopic

  useNextPrevKeyboard({
    isNextDisabled,
    isPreviousDisabled,
    handleBackToPreviousTopic,
    handleGoToNextTopic,
  })

  const initializeAnswers = (_questions: Question[], answeredQuestions: AnsweredQuestion[] = []): void => {
    const indexedAnsweredQuestions: Record<
      string,
      {
        value: AnsweredQuestion['answer']
        skipped: AnsweredQuestion['skipped']
        skipReason?: AnswerSkipReasonEnum
        suggestedAnswer: AnsweredQuestion['suggestedAnswer']
      }
    > = {}
    answeredQuestions.forEach(q => {
      const skipReason = getAnswerSkipReason(q)
      indexedAnsweredQuestions[q.id] = {
        value: q.answer,
        skipped: Boolean(skipReason),
        skipReason,
        suggestedAnswer: q.suggestedAnswer,
      }
    })
    const _indexedAnswers: Record<string, AnswerT> = {}
    _questions.forEach(q => {
      const skipReason = indexedAnsweredQuestions[q.id]?.skipReason
      _indexedAnswers[q.id] = {
        questionId: q.id,
        answer: indexedAnsweredQuestions[q.id]?.value as AnswerT['answer'],
        weight: q.weight,
        skipped: Boolean(skipReason) || q.skipped,
        skipReason,
        suggestedAnswer: indexedAnsweredQuestions[q.id]?.suggestedAnswer as AnswerT['suggestedAnswer'],
      }
    })
    setIndexedAnswers(_indexedAnswers)
  }

  const saveReview = async (enableDraftFlowLogs = false): Promise<Review> => {
    try {
      let currentReviewId = currentReview?.id

      if (enableDraftFlowLogs) {
        await trackDraftFlowEvent(TrackingCategoryEventEnum.ASSESSMENT.SAVE_DRAFT_FLOW_REQUEST, {
          stage: 'prepare_payload',
          existingReviewId: currentReviewId ?? null,
          employeeId: reviewedEmployee?.id ?? null,
          formId: currentForm?.id ?? null,
          topic: currentTopic?.name ?? null,
          answersInState: Object.keys(indexedAnswers).length,
        })
      }

      if (!currentReviewId) {
        if (enableDraftFlowLogs) {
          await trackDraftFlowEvent(TrackingCategoryEventEnum.ASSESSMENT.SAVE_DRAFT_FLOW_REQUEST, {
            stage: 'create_review_request',
            reviewedEmployeeId: reviewedEmployee?.id ?? null,
            formId: currentForm?.id ?? null,
          })
        }

        currentReviewId = await ReviewService.createEmptyReview(
          reviewedEmployee?.id as number,
          currentForm?.id as number
        )

        if (enableDraftFlowLogs) {
          await trackDraftFlowEvent(TrackingCategoryEventEnum.ASSESSMENT.SAVE_DRAFT_FLOW_RESPONSE, {
            stage: 'create_review_response',
            reviewId: currentReviewId,
          })
        }
      }

      if (currentReviewId === undefined) {
        throw new Error('Review ID is required before saving draft answers')
      }
      const reviewIdToSave = currentReviewId

      // Ensure NUMERIC (auto-calculated slider) answers are included even when the
      // SliderQuestion component hasn't rendered for that category (e.g. after a page
      // reload landing on the Final Step).
      const answersWithSliderScores = { ...indexedAnswers }
      Object.entries(groupedQuestions).forEach(([category, questions]) => {
        const numericQuestion = questions.find(q => q.type === QuestionTypeEnum.NUMERIC)
        if (!numericQuestion) return
        const currentAnswer = answersWithSliderScores[numericQuestion.id]
        if (currentAnswer && currentAnswer.answer === undefined) {
          const computedScore = computeCategorySliderScore(category)
          if (computedScore !== undefined) {
            answersWithSliderScores[numericQuestion.id] = {
              ...currentAnswer,
              answer: computedScore,
              suggestedAnswer: computedScore,
            }
          }
        }
      })

      // The save endpoint (PUT /reviews/{id}/answers) REPLACES all answers,
      // so we must always send the complete set — not just changed answers.
      // Previously this only sent "unsaved" (changed) answers, which caused
      // a draft→publish flow to lose all category answers when only the
      // Final Step had changes.
      const answersToSave: AnswerToBeAddedToReview[] = []
      Object.entries(answersWithSliderScores).forEach(([, answer]) => {
        const currentSkipReason = getAnswerSkipReason(answer)
        if (answer.answer === undefined && !currentSkipReason) {
          return
        }
        const answerToSave: AnswerToBeAddedToReview = {
          review: reviewIdToSave,
          question: answer.questionId,
          answer: currentSkipReason
            ? ''
            : typeof answer.answer === 'string'
            ? answer.answer
            : JSON.stringify(answer.answer),
          skipped: Boolean(currentSkipReason),
          skipReason: currentSkipReason,
          suggestedAnswer: answer.suggestedAnswer?.toString(),
        }
        answersToSave.push(answerToSave)
      })

      if (enableDraftFlowLogs) {
        await trackDraftFlowEvent(TrackingCategoryEventEnum.ASSESSMENT.SAVE_DRAFT_FLOW_REQUEST, {
          stage: 'save_answers_request',
          reviewId: currentReviewId,
          answersToSave: answersToSave.length,
          questionIds: answersToSave.map(({ question }) => question),
        })
      }

      trackSaveEvent(answersToSave)
      await ReviewService.saveAnswers(currentReviewId, answersToSave)

      if (enableDraftFlowLogs) {
        await trackDraftFlowEvent(TrackingCategoryEventEnum.ASSESSMENT.SAVE_DRAFT_FLOW_RESPONSE, {
          stage: 'save_answers_response',
          reviewId: currentReviewId,
          answersSaved: answersToSave.length,
        })
        await trackDraftFlowEvent(TrackingCategoryEventEnum.ASSESSMENT.SAVE_DRAFT_FLOW_REQUEST, {
          stage: 'reload_review_request',
          reviewId: currentReviewId,
        })
      }

      const savedReview = await ReviewService.findOne(currentReviewId)

      if (enableDraftFlowLogs) {
        await trackDraftFlowEvent(TrackingCategoryEventEnum.ASSESSMENT.SAVE_DRAFT_FLOW_RESPONSE, {
          stage: 'reload_review_response',
          reviewId: savedReview.id,
          persistedAnswers: savedReview.answers?.length ?? 0,
          finishDate: savedReview.finishDate ?? null,
        })
      }

      setCurrentReview(savedReview)
      return savedReview
    } catch (error) {
      if (enableDraftFlowLogs) {
        await trackDraftFlowErrorEvent(
          {
            stage: 'save_review',
            reviewId: currentReview?.id ?? null,
            employeeId: reviewedEmployee?.id ?? null,
            formId: currentForm?.id ?? null,
            topic: currentTopic?.name ?? null,
          },
          error
        )
      }
      throw error
    }
  }

  const trackSaveEvent = (ansersToAdd: AnswerToBeAddedToReview[]) => {
    const details = {
      totalQuestions: groupedQuestions[currentTopic?.name || ''].length,
      answeredQuestions: ansersToAdd.length,
      reviewID: currentReview?.id,
      category: currentTopic?.name,
    }
    trackEvent(
      TrackingCategoryEnum.ASSESSMENT,
      TrackingCategoryEventEnum.ASSESSMENT.CLICK_SAVE_BTN,
      JSON.stringify(details)
    )
  }

  const getQuestionsDependsOnReviewer = (form: Form, userId: number | undefined, reviewerId: number | undefined) => {
    if (userId === reviewerId) {
      return form.questions?.filter((question: Question) => !question.hiddenWhenAuthorIsTheReviewedEmployee) || []
    }
    return form.questions || []
  }

  // this function gets the user's reviews and filters them
  // by the same form, discards the current review if we are
  // editing it, and picks the most recent one OR if we are
  // editing a review, the most recent one before the current one
  const getPreviousReview = (currentForm: Form): Review | undefined => {
    const previousReview = reviewsFromUser
      .filter(review => (review.form.id === currentForm.id && currentReview ? currentReview.id !== review.id : true))
      .sort((a, b) => (a.finishDate ?? '').localeCompare(b.finishDate ?? ''))
      .at(-1)
    return previousReview ?? undefined
  }

  const renderForm = async (id: number) => {
    setLoading(true)
    const currentForm = await FormService.findById(id)
    const allQuestions = getQuestionsDependsOnReviewer(currentForm, user?.id, reviewedEmployee?.id)
    const sortedQuestions = _sortQuestions(allQuestions)
    const _groupedQuestions = _groupQuestionsByTopic(allQuestions)
    const _indexedQuestions = _indexQuestions(allQuestions)
    initializeAnswers(allQuestions, currentReview?.answers)
    setGroupedQuestions(_groupedQuestions)
    setIndexedQuestions(_indexedQuestions)
    setSelectedFormId(id)
    const sortedTopics = _sortTopics(sortedQuestions)
    setTopics(sortedTopics)
    // to indicate previous answers to the same form by the same author to the same user,
    // we need to check that we are creating a new review, or editing an upublished one
    // (which will always be the latest one)
    const workingOnUnpublishedReview = (currentReview && !currentReview.finishDate) || !currentReview
    if (workingOnUnpublishedReview && reviewsFromUser) {
      const _lastReviewFromUser = getPreviousReview(currentForm)
      _lastReviewFromUser && setQuestionsPreviousAnswers(_storePreviousAnswers(_indexedQuestions, _lastReviewFromUser))
    }
    setLoading(false)
  }

  const deleteReview = async (id: number) => await ReviewService.deleteReview(id)

  useEffect(() => {
    if (currentForm && currentForm.id !== undefined) {
      renderForm(currentForm.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentForm, allForms])

  const _initReview = async (): Promise<void> => {
    try {
      setLoading(true)
      const review = reviewId
        ? await ReviewService.findOne(Number(reviewId)).then((r: Review) => {
            setCurrentReview(r)
            return r
          })
        : null

      if (review?.finishDate && review.reviewRequest && review?.reviewAuthor?.id === user?.id) {
        history.push(createPathWithLang(NetworkConstants.URL_REVIEW_FINISHED_MSG))
      }

      const _employeeId = review?.reviewedEmployee?.id || Number(employeeId)
      const employee = await EmployeeService.findOne(_employeeId)
      setReviewedEmployee(employee)

      const rawReviews = await ReviewService.findByEmployeeId(_employeeId)
      const finishedReviews = rawReviews.filter(review => review.finishDate)
      const lastForm = finishedReviews[finishedReviews.length - 1]?.form
      const reviewsFromUser = finishedReviews && finishedReviews.filter(review => review.reviewAuthor.id === user?.id)
      setLastFormUsed(lastForm)
      setReviewsFromUser(reviewsFromUser)

      const forms = await FormService.findAll()
      const formCategories = await FormService.findAllByCategory('review')
      setAllForms(forms)
      setAllFormCategories(sortFormCategories(formCategories))
      if (reviewId && forms) {
        const _currentFormId = review?.form?.id || 1
        setCurrentForm(getFormById(_currentFormId, forms))
        setSelectedFormId(_currentFormId)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const _indexQuestions = (questions: Question[] = []): Record<string, Question> => {
    const _indexedQuestions = {} as Record<string, Question>
    questions.forEach(question => {
      _indexedQuestions[question.id] = question
    })

    return _indexedQuestions
  }

  const _groupQuestionsByTopic = (questions: Question[] = []): Record<string, Question[]> => {
    const _groupedQuestions = {} as Record<string, Question[]>
    questions.forEach(question => {
      const topic = question.topic?.name
      if (topic) {
        _groupedQuestions[topic] = [...(_groupedQuestions[topic] || []), question]
      }
    })

    return _groupedQuestions
  }

  const _sortTopics = (questions: Question[] = []): QuestionTopic[] => {
    const topicsObj: Record<string, QuestionTopic> = questions.reduce(
      (topics, { topic }) => (topic?.order ? { ...topics, [topic.order]: topic } : topics),
      {}
    )
    return Object.values(topicsObj)
  }

  const _storePreviousAnswers = (
    indexedQuestions: Record<string, Question>,
    lastReviewFromUser: Review
  ): Record<string, AnsweredQuestion> => {
    const previousAnswers = {} as Record<string, AnsweredQuestion>
    for (const key in indexedQuestions) {
      const answer = lastReviewFromUser.answers.find(answer => answer.id === indexedQuestions[key].id)
      if (answer) previousAnswers[key] = answer
    }
    return previousAnswers
  }

  useEffect(() => {
    void _initReview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, reviewId])

  useEffect(() => {
    setUnfinishedTopic(isTopicUnfinished(currentTopic?.name || ''))
    const questionsColumn = divContentQuestionsRef.current
    let columnHeight = 0
    if (questionsColumn) {
      for (const child of questionsColumn.children) {
        columnHeight += child.clientHeight
      }
    }
    setShowBackToTop(columnHeight > window.innerHeight)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTopic])

  const isTopicUnfinished = (topic: string): boolean => {
    if (topic === '' || groupedQuestions === undefined) {
      return false
    }
    const topicQuestions = groupedQuestions[topic]
    const topicAnswers = topicQuestions.map(question => indexedAnswers[question.id])
    const topicAnswersCompleted = areAllRequiredQuestionsAnswered(topicAnswers)
    return !topicAnswersCompleted && areAnyQuestionsAnswered(topicAnswers)
  }

  useEffect(() => {
    // Recalculate slider values for ALL topics with NUMERIC questions, not just the current topic.
    // This ensures slider values are available after a page reload or draft save even when landing
    // on the Final Step (which has no NUMERIC questions and would leave other categories' sliders empty).
    const updatedSliderValues: SliderValues = {}
    Object.entries(groupedQuestions).forEach(([, questions]) => {
      const sliderQuestion = questions.find(question => question.type === QuestionTypeEnum.NUMERIC)
      if (!sliderQuestion) return

      const questionSummary = questions
        .filter(({ id, type }) => type === QuestionTypeEnum.SCALE && !isAnswerSkipped(indexedAnswers[id]))
        .map(({ id, weight, min = 1, max = 4 }) => ({
          id,
          min,
          max,
          weight: weight ?? 1, // TODO verify assumption that value is 1 (as opposed to 0) if no value is set
          value: (indexedAnswers[id]?.answer as number) ?? 1, // 1 being the lowest score, 0 breaks everything
        }))
      let totalWeight = 0
      let totalValues = 0
      let newValue = 0
      if (questionSummary.length) {
        totalWeight = questionSummary?.reduce((total, question) => total + (question.weight ?? 1), 0)
        totalValues = questionSummary?.reduce((total, question) => {
          const proRatedValue = (question.value - question.min) / (question.max - question.min)
          return total + proRatedValue * question.weight
        }, 0)
        newValue = Math.round((totalValues / totalWeight) * 100)
      }
      updatedSliderValues[sliderQuestion.id] = {
        hasUserAnswer: questionSummary.some(({ id }) => indexedAnswers[id]?.answer !== undefined),
        value: newValue,
      }
      // FIXME: when all the scales are skipped the slider is fixed to 0. What should we do?
      // Skip slider? (what happen then when comparing reviews?)
      // Set to some value? This will affect the average
    })
    setSliderValues(updatedSliderValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indexedAnswers, groupedQuestions])

  const handleSave = async (finish = true): Promise<void> => {
    try {
      setLoading(true)
      await trackDraftFlowEvent(TrackingCategoryEventEnum.ASSESSMENT.SAVE_DRAFT_FLOW_CLICKED, {
        reviewId: currentReview?.id ?? null,
        employeeId: reviewedEmployee?.id ?? Number(employeeId),
        formId: currentForm?.id ?? selectedFormId ?? null,
        topic: currentTopic?.name ?? null,
        finish,
      })

      const savedReview = await saveReview(true)
      if (!reviewId) {
        const editPath = createPathWithLang(NetworkConstants.URL_EDIT_REVIEW).replace(
          ConstantIDs.REVIEW_ID_PATH_PLACEHOLDER,
          savedReview.id.toString()
        )
        history.replace(`${editPath}?${query.toString()}`)
        return
      }

      if (!currentTopic || !getCompletedCategories().includes(currentTopic.name)) {
        setUnfinishedTopic(isTopicUnfinished(currentTopic?.name || ''))
      }

      changeTopic(currentTopicIndex)
    } catch (error) {
      await trackDraftFlowErrorEvent(
        {
          stage: 'handle_save',
          reviewId: currentReview?.id ?? null,
          employeeId: reviewedEmployee?.id ?? Number(employeeId),
          formId: currentForm?.id ?? selectedFormId ?? null,
          topic: currentTopic?.name ?? null,
        },
        error
      )
      setShowErrorModal(true)
      console.error(error)
    } finally {
      if (reviewId) {
        setLoading(false)
      }
    }
  }

  const scoredCategoryNames = useMemo(
    () => getScoredCategoryNames(Object.values(indexedQuestions || {})),
    [indexedQuestions]
  )

  const talentRankQuestion = useMemo(() => {
    return findTalentRankQuestion(Object.values(indexedQuestions || {}))
  }, [indexedQuestions])

  const areAllRequiredQuestionsAnswered = (answers: AnswerT[] = []): boolean => {
    return !answers.some(answer => {
      const questionData = indexedQuestions[answer?.questionId]
      const topicQuestions = questionData?.topic?.name ? groupedQuestions[questionData.topic.name] || [] : []
      const relatedQuestionId = questionData?.relatedQuestionId
      const relatedQuestionTargetOptions = questionData?.relatedQuestionTargetOptions
      const matchesOverrideJustificationTitle = isOverrideJustificationTitle(questionData?.title)
      const matchesOverrideJustificationTarget =
        relatedQuestionTargetOptions?.some(option => option.toLowerCase() === '__not_suggested__') ?? false
      const isOverrideJustificationQuestion =
        talentRankQuestion?.id === relatedQuestionId &&
        (matchesOverrideJustificationTitle || matchesOverrideJustificationTarget)
      const suggestedTalentRankValue = talentRankSuggestion
      const talentRankAnswer = talentRankQuestion?.id ? indexedAnswers[talentRankQuestion.id]?.answer : undefined
      const shouldForceOverrideJustificationRequirement =
        isOverrideJustificationQuestion &&
        suggestedTalentRankValue !== undefined &&
        talentRankAnswer !== undefined &&
        talentRankAnswer !== suggestedTalentRankValue
      const shouldIgnoreAutoCalculatedScoreRequirement = shouldTreatAutoCalculatedScoreAsOptional(
        questionData,
        topicQuestions,
        indexedAnswers
      )
      const isRequiredQuestion =
        !shouldIgnoreAutoCalculatedScoreRequirement &&
        (questionData?.required || shouldForceOverrideJustificationRequirement) &&
        (questionData?.skippable === undefined || !isAnswerSkipped(answer))
      return isRequiredQuestion && (answer?.answer === undefined || answer?.answer === '')
    })
  }

  const areAnyQuestionsAnswered = (answers: AnswerT[] = []): boolean => {
    return answers.some(answer => {
      return (answer.answer !== undefined && answer.answer !== '') || isAnswerSkipped(answer)
    })
  }

  const handleFinishReview = async (): Promise<void> => {
    try {
      setLoading(true)
      if (!areAllRequiredQuestionsAnswered(Object.values(indexedAnswers))) {
        setShowErrorModal(true)
        return
      }
      const savedReview = await saveReview()
      trackEvent(
        TrackingCategoryEnum.ASSESSMENT,
        TrackingCategoryEventEnum.ASSESSMENT.CLICK_FINISH_REVIEW_BTN,
        JSON.stringify({ reviewId: savedReview.id })
      )
      await ReviewService.finishReview(savedReview.id)
      if ((user?.id || savedReview.reviewAuthor.id) === savedReview.reviewedEmployee.id) {
        history.push(createPathWithLang(NetworkConstants.URL_TECH_PROFILE))
        return
      }
      const resultPath = !currentReview?.reviewRequest
        ? createPathWithLang(NetworkConstants.URL_REPORT_FORM)
            .replace(
              ConstantIDs.EMPLOYEE_ID_PATH_PLACEHOLDER,
              currentReview?.reviewedEmployee.id.toString() || employeeId
            )
            .replace(ConstantIDs.FORM_ID_PATH_PLACEHOLDER, selectedFormId?.toString() || '')
        : createPathWithLang(NetworkConstants.URL_REVIEW_FINISHED_MSG)
      history.push(resultPath)
    } catch (error) {
      setShowErrorModal(true)
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleFinishEditingReview = async () => {
    checkIfExistUnsavedAnswers(indexedAnswers, currentReview) && (await saveReview())
    if ((user?.id || currentReview?.reviewAuthor.id) === currentReview?.reviewedEmployee.id) {
      history.push(createPathWithLang(NetworkConstants.URL_TECH_PROFILE))
      return
    }
    const resultPath = !currentReview?.reviewRequest
      ? createPathWithLang(NetworkConstants.URL_REPORT_FORM)
          .replace(
            ConstantIDs.EMPLOYEE_ID_PATH_PLACEHOLDER,
            currentReview?.reviewedEmployee.id.toString() || employeeId
          )
          .replace(ConstantIDs.FORM_ID_PATH_PLACEHOLDER, selectedFormId?.toString() || '')
      : createPathWithLang(NetworkConstants.URL_REVIEW_FINISHED_MSG)
    history.push(resultPath)
  }

  const handleCategorySelected = (categorySelected: string): void => {
    const topicIdx = topics.findIndex(({ name }) => categorySelected === name)
    trackEvent(
      TrackingCategoryEnum.ASSESSMENT,
      TrackingCategoryEventEnum.ASSESSMENT.CLICK_CATEGORY_BTN,
      JSON.stringify({
        category: topicIdx,
        reviewId: currentReview?.id,
        totalQuestions: Object.values(indexedAnswers).length,
        answeredQuestions: Object.values(indexedAnswers).filter(({ answer }) => answer !== undefined).length,
      })
    )

    changeTopic(topicIdx)
  }

  const changeTopic = (topicIdx: number) => {
    query.set('topic', `${topicIdx}`)
    history.replace({
      search: query.toString(),
    })
    window.scrollTo(0, 0)
  }

  const handleSaveSuggestedSlider = (questionId: number) => (value: number | undefined) => {
    const answer = indexedAnswers[questionId]
    if (answer) answer.suggestedAnswer = value
    setIndexedAnswers({ ...indexedAnswers })
  }

  const handleQuestionAnswered = (questionId: number) => (value: unknown, skipped?: boolean) => {
    const answer = indexedAnswers[questionId]
    if (answer) answer.answer = value as AnswerT['answer']
    if (skipped !== undefined) {
      answer.skipped = skipped
      answer.skipReason = skipped ? answer.skipReason : undefined
    }
    setIndexedAnswers({ ...indexedAnswers })
  }

  const handleScaleQuestionAnswered =
    (questionId: number) =>
    (value?: number, skipReason?: AnswerSkipReasonEnum): void => {
      const answer = indexedAnswers[questionId]
      if (!answer) {
        return
      }

      answer.answer = skipReason ? undefined : value
      answer.skipReason = skipReason
      answer.skipped = Boolean(skipReason)
      setIndexedAnswers({ ...indexedAnswers })
    }

  const handleMultiQuestionAnswered = (questionId: number) => (value: string[], skipped?: boolean) => {
    const uniqueValues = [...new Set(value)]
    handleQuestionAnswered(questionId)(uniqueValues.length > 0 ? uniqueValues : undefined, skipped)
  }

  const handleDeleteReview = () => {
    trackEvent(TrackingCategoryEnum.ASSESSMENT, TrackingCategoryEventEnum.ASSESSMENT.CLICK_DELETE_REVIEW_LINK, '')
    setShowDeleteModal(true)
  }
  const handleCancelDelete = () => {
    trackEvent(
      TrackingCategoryEnum.ASSESSMENT,
      TrackingCategoryEventEnum.ASSESSMENT.CLICK_DELETE_REVIEW_ACTION,
      JSON.stringify({ selectedAction: 'cancel' })
    )
    setShowDeleteModal(false)
  }
  const handleConfirmDelete = async () => {
    try {
      trackEvent(
        TrackingCategoryEnum.ASSESSMENT,
        TrackingCategoryEventEnum.ASSESSMENT.CLICK_DELETE_REVIEW_ACTION,
        JSON.stringify({ selectedAction: 'confirm' })
      )
      setIndexedAnswers({})
      if (currentReview?.id) {
        const reviewIdToDelete = currentReview.id
        await deleteReview(reviewIdToDelete)
        if ((user?.id || currentReview?.reviewAuthor.id) === currentReview?.reviewedEmployee.id) {
          history.push(createPathWithLang(NetworkConstants.URL_TECH_PROFILE))
          return
        }
        const reviewListRoute = createPathWithLang(NetworkConstants.URL_REVIEW_LIST)
        history.push(reviewListRoute)
        return
      }

      history.goBack()
    } catch (error) {
      setShowErrorModal(true)
      console.error(error)
    }
  }

  const getCompletedCategories = (): string[] => {
    return topics.reduce<string[]>((reminder, topic) => {
      const topicQuestions = groupedQuestions[topic.name]
      const topicAnswers = topicQuestions.map(question => indexedAnswers[question.id])
      const topicAnswersCompleted = areAllRequiredQuestionsAnswered(topicAnswers)
      return topicAnswersCompleted ? [...reminder, topic.name] : reminder
    }, [])
  }

  const getUnfinishedCategories = (): string[] => {
    return topics.reduce<string[]>((reminder, topic) => {
      const topicQuestions = groupedQuestions[topic.name]
      const topicAnswers = topicQuestions.map(question => indexedAnswers[question.id])
      const topicAnswersCompleted = areAllRequiredQuestionsAnswered(topicAnswers)
      return !topicAnswersCompleted && areAnyQuestionsAnswered(topicAnswers) ? [...reminder, topic.name] : reminder
    }, [])
  }

  const areScoredCategoriesFinished = (): boolean => {
    const completedCategories = getCompletedCategories().map(category => category.toLowerCase())
    return scoredCategoryNames.every(category => completedCategories.includes(category.toLowerCase()))
  }

  const onFormSelectorChange = (value: number) => {
    trackEvent(
      TrackingCategoryEnum.ASSESSMENT,
      TrackingCategoryEventEnum.ASSESSMENT.CLICK_FORM_OPTION,
      JSON.stringify({ formId: value })
    )
    setSelectedFormId(value)
    if (currentForm && value !== currentForm.id) {
      const allAnswers = []
      for (const key in indexedAnswers) {
        allAnswers.push(indexedAnswers[key])
      }
      areAnyQuestionsAnswered(allAnswers) ? setShowChangeFormModal(true) : confirmFormChange(value)
    }
  }

  const cancelFormChange = () => {
    currentForm?.id && setSelectedFormId(currentForm.id)
    setShowChangeFormModal(false)
  }

  const resetReview = () => {
    currentReview &&
      ReviewService.resetReview(currentReview.id, currentReview.form.id || -1).then(() => {
        _initReview()
      })
  }

  const confirmFormChange = (formId = selectedFormId) => {
    if (formId !== undefined) {
      const form = getFormById(formId, allForms ?? [])
      if (currentReview && form) {
        currentReview.form = form
        resetReview()
        currentReview.answers = []
      }
      if (currentTopicToDisplay) {
        resetTopic()
      }
      formId && setCurrentForm(form)
    }
    setShowChangeFormModal(false)
  }

  const handleConfirmExit = (path: string) => {
    history.push({
      pathname: path,
    })
  }

  const handleCanExit = (): boolean => {
    const existUnsavedAnswers = checkIfExistUnsavedAnswers(indexedAnswers, currentReview)
    return !existUnsavedAnswers
  }

  const areRequiredQuestionsAnswered = areAllRequiredQuestionsAnswered(Object.values(indexedAnswers) ?? [])
  const existAnswers = Boolean(Object.keys(indexedAnswers).length)
  const renderFinishReleaseButton = (): JSX.Element => {
    return (
      <div className="flex flex-col flex-wrap items-center">
        <AppButton
          onClick={!currentReview?.finishDate ? handleFinishReview : handleFinishEditingReview}
          disabled={!areRequiredQuestionsAnswered || !existAnswers}
          className={isTutorialCompleted ? 'self-start' : 'm-6'}
          type="clear"
        >
          Publish
        </AppButton>
      </div>
    )
  }

  const arrowLabelProps = { ...(isLastTopic && { nextArrowLabel: 'Finish' }) }
  const handleGoTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })

  const handleAutoFillClickEvent = () => {
    fillAnswersClickCounter++
    if (fillAnswersClickCounter > 4) {
      setLoading(true)
      const questions: Question[] = Object.entries(indexedQuestions).map(value => value[1])
      const newIndexedAnswers = JSON.parse(JSON.stringify(indexedAnswers))
      if (areAllRequiredQuestionsAnswered(Object.values(newIndexedAnswers) || [])) {
        newIndexedAnswers[questions[1].id].answer = undefined
      } else {
        fillAnswersRandomly(questions, newIndexedAnswers)
      }
      setIndexedAnswers({ ...newIndexedAnswers })
      fillAnswersClickCounter = 0
      handleCategorySelected(currentTopic.name)
      setLoading(false)
    }
    if (fillAnswersClickCounter === 1) {
      setTimeout(() => {
        fillAnswersClickCounter = 0
      }, 2000)
    }
  }

  const computeCategorySliderScore = (category: string): number | undefined => {
    const questions = groupedQuestions[category] || []
    const scaleQuestions = questions
      .filter(({ id, type }) => type === QuestionTypeEnum.SCALE && !isAnswerSkipped(indexedAnswers[id]))
      .map(({ id, weight, min = 1, max = 4 }) => ({
        id,
        min,
        max,
        weight: weight ?? 1,
        value: (indexedAnswers[id]?.answer as number) ?? 1,
      }))
    if (scaleQuestions.length === 0) return undefined
    const totalWeight = scaleQuestions.reduce((total, q) => total + (q.weight ?? 1), 0)
    const totalValues = scaleQuestions.reduce((total, q) => {
      const proRatedValue = (q.value - q.min) / (q.max - q.min)
      return total + proRatedValue * q.weight
    }, 0)
    return Math.round((totalValues / totalWeight) * 100)
  }

  const talentRankSummary: TalentRankSummary | undefined = useMemo(() => {
    if (!talentRankQuestion) {
      return undefined
    }

    const optionValues = talentRankQuestion.options || []
    if (!areScoredCategoriesFinished()) {
      return undefined
    }

    const categoryScores = scoredCategoryNames.map(category => {
      const scoreQuestion = (groupedQuestions[category] || [])
        .filter(question => question.type === QuestionTypeEnum.NUMERIC)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .at(-1)

      if (!scoreQuestion?.id) {
        return undefined
      }

      const questionId = scoreQuestion.id
      const answer = indexedAnswers[questionId]
      const availableScores = [
        typeof answer?.answer === 'number' ? answer.answer : undefined,
        typeof answer?.suggestedAnswer === 'number' ? answer.suggestedAnswer : undefined,
        sliderValues[questionId]?.value,
        computeCategorySliderScore(category),
      ]
      const score = availableScores.find(value => typeof value === 'number')
      return typeof score === 'number' ? Math.max(0, Math.min(100, Math.round(score))) : undefined
    })

    if (categoryScores.some(score => score === undefined)) {
      return undefined
    }

    return calculateTalentRankSummary({
      scores: categoryScores as number[],
      options: optionValues,
      fallbackLabels: DEFAULT_TALENT_RANK_FALLBACK_LABELS,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentRankQuestion, indexedAnswers, sliderValues, groupedQuestions, scoredCategoryNames, topics])

  useEffect(() => {
    if (!talentRankQuestion?.id) {
      return
    }
    const suggestion = talentRankSummary?.suggestionValue
    setTalentRankSuggestion(prevSuggestion => (prevSuggestion === suggestion ? prevSuggestion : suggestion))
    if (!suggestion) {
      return
    }
    setIndexedAnswers(prevIndexedAnswers => {
      const currentAnswer = prevIndexedAnswers[talentRankQuestion.id]
      if (currentAnswer?.answer === suggestion) {
        return prevIndexedAnswers
      }
      const hasStoredAnswer =
        currentAnswer?.answer !== undefined &&
        currentAnswer?.answer !== null &&
        (typeof currentAnswer.answer !== 'string' || currentAnswer.answer.trim().length > 0)
      if (hasStoredAnswer) {
        return prevIndexedAnswers
      }
      const updatedAnswer = currentAnswer
        ? { ...currentAnswer, answer: suggestion, skipped: false }
        : {
            questionId: talentRankQuestion.id,
            answer: suggestion,
            weight: talentRankQuestion.weight,
            skipped: false,
            suggestedAnswer: undefined,
          }
      return {
        ...prevIndexedAnswers,
        [talentRankQuestion.id]: updatedAnswer,
      }
    })
  }, [talentRankQuestion, talentRankSummary?.suggestionValue, setIndexedAnswers])

  const currentTopicName = currentTopic?.name || ''
  const currentTopicQuestions = groupedQuestions[currentTopicName] || []

  if (loading) {
    return <Spinner />
  }
  return (
    <div className="container">
      <TutorialModal
        visible={!introductionView && user?.role !== 'user'}
        onClose={() => {
          setIntroductionView(true)
          const currentTutorial =
            selectedFormId === undefined ? 'review-new-layout-tutorial-step-1' : 'review-tutorial-step-1'
          dispatch(showHelpId(currentTutorial))
        }}
        skipTutorial={() => {
          setIntroductionView(true)
          dispatch(finishTutorial('review-tutorial-step-1'))
        }}
        icon={false}
        message={<TutorialIntroduction />}
        startButtonText="Quick tutorial"
      />
      <TutorialModal
        visible={!userIntroductionView && user?.role === 'user' && selectedFormId !== undefined}
        onClose={() => {
          setUserIntroductionView(true)
          dispatch(showHelpId('user-assesment-tutorial-step-1'))
        }}
        skipTutorial={() => {
          setUserIntroductionView(true)
          dispatch(finishTutorial('user-assesment-tutorial-step-1'))
        }}
        icon={false}
        message={<UserTutorialIntroduction />}
        startButtonText="Quick tutorial"
      />
      <NavigationBlocker
        modalMessage={exitWithoutChangesModalMessage}
        securePathRegExp={REVIEW_FORM_MAIN_PATH}
        checkIfCanNavigate={handleCanExit}
        onConfirmNavigation={handleConfirmExit}
        isRefreshSensitive={true}
      />
      <div className="flex flex-col pb-14 gap-10">
        <div className={classNames('flex items-end relative gap-1', { 'flex-wrap': selectedFormId === undefined })}>
          <div
            className={classNames('flex min-w-96 flex-col w-1/3', {
              'basis-full': selectedFormId === undefined,
            })}
          >
            {!selectedFormId && <span className="text-6xl text-dark-blue font-semibold m-0 leading-0">Assessment</span>}
          </div>
          <div
            className={classNames('flex flex-col flex-1 gap-y-5', {
              'mt-10': selectedFormId === undefined,
            })}
          >
            <div className="flex flex-row w-full gap-0 items-end">
              {selectedFormId === undefined && <p className="text-[2.4rem] font-normal flex-1">Select the form type</p>}
              <p className="text-2xl font-bold text-stone-gray flex-1">Team Member</p>
              <p className="text-2xl font-bold text-stone-gray flex-1">Job Title</p>
            </div>
            <div className="flex flex-row w-full gap-0 items-end">
              <p className={classNames({ 'flex-1': selectedFormId === undefined })}>
                {selectedFormId === undefined && lastFormUsed && (
                  <span className="text-2xl font-normal text-stone-gray mt-5 clock-icon-preceded">
                    Last form used: {lastFormUsed?.name}
                  </span>
                )}
              </p>
              <p className="text-4xl text-dark-blue font-medium flex-1">{reviewedEmployee?.name}</p>
              <p className="text-4xl text-dark-blue font-medium flex-1">{reviewedEmployee?.jobTitle}</p>
            </div>
          </div>
        </div>
        {selectedFormId === undefined ? (
          <div>
            <EmployeeReviewFormSelectionStep
              employeeId={employeeId}
              lastFormUsed={lastFormUsed}
              formCategories={allFormCategories ?? []}
              onConfirm={formId => {
                setSelectedFormId(formId)
                setCurrentForm(getFormById(formId, allForms ?? []))
              }}
              showTitle={false}
              disableSkipAssessment={false}
            />
          </div>
        ) : (
          <div className="flex flex-1 relative gap-2">
            <div className="flex flex-col w-1/3 min-w-96 relative -top-20">
              <div className="sticky top-8">
                <div className="flex items-start w-1/3 min-w-96 flex-col gap-6 mb-10">
                  <h2
                    onClick={handleAutoFillClickEvent}
                    className="text-dark-blue font-semibold text-6xl m-0 leading-0"
                  >
                    Assessment
                  </h2>
                  <div
                    onClick={() => {
                      trackEvent(
                        TrackingCategoryEnum.ASSESSMENT,
                        TrackingCategoryEventEnum.ASSESSMENT.CLICK_FORM_SELECTOR,
                        ''
                      )
                    }}
                  >
                    {(allForms?.length || 0) > 1 && (
                      <HelpTooltip id={'review-tutorial-step-1'}>
                        <AppSelect
                          options={mapFormsToFormSelectOptions(allForms ?? [])}
                          onChange={value => onFormSelectorChange(Number(value))}
                          value={selectedFormId.toString()}
                          sortBy="value"
                          className="w-[30rem] xl:w-[38rem] !z-50"
                        />
                      </HelpTooltip>
                    )}
                  </div>
                </div>
                <CategorySelector
                  categories={topics.map(({ name }) => name)}
                  completedCategories={getCompletedCategories()}
                  selectedCategory={currentTopic?.name}
                  unfinishedCategories={getUnfinishedCategories()}
                  onCategorySelected={handleCategorySelected}
                  tutorialReview
                />
                <div className="mt-[6rem]">
                  <HelpTooltip
                    id={'review-tutorial-step-7'}
                    childrenClassNames="relative z-50 bg-white w-fit items-center flex-1"
                    placement="top-end"
                  >
                    <div className="flex justify-start items-start gap-2 mb-8">
                      <div className="flex flex-col justify-start">
                        <HelpTooltip
                          id={'user-assesment-tutorial-step-6'}
                          childrenClassNames="relative z-50 bg-white w-fit items-center flex-1"
                          placement="top-end"
                        >
                          <AppButton type="info" onClick={handleSave} className={isTutorialCompleted ? '' : 'm-6'}>
                            Save draft
                          </AppButton>
                        </HelpTooltip>

                        {user && reviewedEmployee && (
                          <button onClick={handleDeleteReview} className="flex items-center mt-4">
                            <GarbageSVG color="#EC6823" width={13} height={15} />
                            <span className="invisible lg:visible text-orange text-2xl font-semibold ml-3 font-manrope">
                              Delete assessment
                            </span>
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap">
                        <HelpTooltip
                          id={'user-assesment-tutorial-step-7'}
                          childrenClassNames="relative z-50 bg-white w-fit items-center flex-1"
                          placement="top-end"
                        >
                          {renderFinishReleaseButton()}
                        </HelpTooltip>
                      </div>
                    </div>
                  </HelpTooltip>
                </div>
              </div>
            </div>
            <div className="flex flex-col flex-1" ref={divContentQuestionsRef}>
              <p className="text-4xl font-medium text-orange mb-10">{currentTopic?.name}</p>
              <p className="max-w-[90rem] text-xl leading-[1.8] text-stone-gray mb-8">
                Use <span className="font-semibold text-dark-blue">Not applicable</span> only when the behavior truly
                does not apply to the role, and <span className="font-semibold text-dark-blue">No visibility</span> only
                when you have not had sufficient opportunity to observe. Otherwise, please{' '}
                <span className="font-semibold text-dark-blue">select the rating that best fits</span>.
              </p>
              <div className="flex flex-wrap justify-between gap-1 mb-24 overflow-y-auto">
                {indexedAnswers &&
                  currentTopicQuestions.map((question, i) => {
                    switch (question.type) {
                      case QuestionTypeEnum.SCALE:
                        const showTutorial = i === 1 && !isTutorialCompleted
                        return (
                          <SelectQuestion
                            key={`employee-review-${currentTopic}-question-${i}`}
                            value={
                              typeof indexedAnswers[question.id]?.answer === 'number'
                                ? (indexedAnswers[question.id]?.answer as number)
                                : undefined
                            }
                            index={i + 1}
                            max={question.max}
                            min={question.min}
                            title={question.title}
                            description={question.description}
                            minLabel={question.labels[0]}
                            maxLabel={question.labels[1]}
                            unfinished={isUnfinishedTopic}
                            required={question.required}
                            skippable={question.skippable}
                            skipReason={getAnswerSkipReason(indexedAnswers[question.id])}
                            onChange={handleScaleQuestionAnswered(question.id)}
                            tutorialButton={showTutorial}
                          />
                        )
                      case QuestionTypeEnum.NUMERIC:
                        if (!isTutorialCompleted) {
                          return (
                            <HelpTooltip
                              id={user?.role === 'user' ? 'user-assesment-tutorial-step-5' : 'review-tutorial-step-6'}
                              containerClassNames="flex flex-row-reverse w-full"
                              childrenClassNames="relative z-50 flex flex-wrap w-full items-center p-6"
                              placement="top-start"
                              key={SLIDER_QUESTION_ID}
                            >
                              <SliderQuestion
                                key={SLIDER_QUESTION_ID}
                                value={indexedAnswers[question.id]?.answer as number}
                                suggested={
                                  sliderValues[question.id]?.hasUserAnswer
                                    ? sliderValues[question.id]?.value
                                    : undefined
                                }
                                index={i + 1}
                                max={question.max}
                                min={question.min}
                                title={question.title}
                                description={question.description}
                                minLabel={question.labels ? question.labels[0] : ''}
                                maxLabel={question.labels ? question.labels[1] : ''}
                                unfinished={isUnfinishedTopic && indexedAnswers[question.id]?.answer === undefined}
                                required={question.required}
                                onChange={handleQuestionAnswered(question.id)}
                                onUpdateSuggested={handleSaveSuggestedSlider(question.id)}
                              />
                            </HelpTooltip>
                          )
                        }
                        return (
                          <SliderQuestion
                            key={SLIDER_QUESTION_ID}
                            value={indexedAnswers[question.id]?.answer as number}
                            suggested={
                              sliderValues[question.id]?.hasUserAnswer ? sliderValues[question.id]?.value : undefined
                            }
                            index={i + 1}
                            max={question.max}
                            min={question.min}
                            title={question.title}
                            description={question.description}
                            minLabel={question.labels ? question.labels[0] : ''}
                            maxLabel={question.labels ? question.labels[1] : ''}
                            unfinished={isUnfinishedTopic && indexedAnswers[question.id]?.answer === undefined}
                            required={question.required}
                            onChange={handleQuestionAnswered(question.id)}
                            onUpdateSuggested={handleSaveSuggestedSlider(question.id)}
                          />
                        )
                      case QuestionTypeEnum.DESCRIPTIVE_RATE:
                        return (
                          <SliderTextQuestion
                            key={`employee-review-${currentTopic}-question-${i}`}
                            index={i + 1}
                            title={question.title}
                            labels={question.labels || []}
                            unfinished={isUnfinishedTopic}
                            required={question.required}
                            onChange={handleQuestionAnswered(question.id)}
                          />
                        )
                      case QuestionTypeEnum.MULTIPLE_SELECTION:
                        return (
                          <ChecklistQuestion
                            key={`employee-review-${currentTopic}-question-${i}`}
                            selectedOptions={(indexedAnswers[question.id]?.answer as string[]) || []}
                            title={question.title}
                            options={question.options.map(opt => ({ label: opt, value: opt }))}
                            customOptionsPlaceholder={question.description}
                            unfinished={isUnfinishedTopic}
                            required={question.required}
                            skippable={question.skippable}
                            skipped={indexedAnswers[question.id]?.skipped as boolean}
                            onChange={handleMultiQuestionAnswered(question.id)}
                          />
                        )
                      case QuestionTypeEnum.SINGLE_SELECTION:
                        const options = question.options.map(opt => {
                          const labelValue = `${opt} ${iconsEmployee[opt] || ''}`
                          return { label: labelValue, value: opt }
                        })
                        const isTalentRankQuestion =
                          isTalentRankTitle(question.title) || isTalentRankTitle(question.label)
                        const talentRankSummaryForQuestion = isTalentRankQuestion ? talentRankSummary : undefined
                        const suggestedTalentRankOption = talentRankSummaryForQuestion?.suggestionValue

                        // Set the suggested answer for talent rank question
                        if (isTalentRankQuestion && suggestedTalentRankOption && indexedAnswers[question.id]) {
                          indexedAnswers[question.id].suggestedAnswer = suggestedTalentRankOption
                        }

                        return (
                          <DropdownQuestion
                            key={`employee-review-${currentTopic}-question-${i}`}
                            value={(indexedAnswers[question.id]?.answer as string) || ''}
                            title={question.title}
                            placeholder={question.description}
                            options={options}
                            unfinished={isUnfinishedTopic}
                            required={question.required}
                            onChange={handleQuestionAnswered(question.id)}
                            previousAnswer={
                              questionsPreviousAnswers &&
                              (questionsPreviousAnswers[Number(question.id)]?.answer as string)
                            }
                            suggested={Boolean(suggestedTalentRankOption)}
                            suggestedOptionValue={suggestedTalentRankOption}
                            talentRankSummary={talentRankSummaryForQuestion}
                          />
                        )
                      case QuestionTypeEnum.PARAGRAPH:
                        const { relatedQuestionId, relatedQuestionTargetOptions } = question
                        let showQuestion = true
                        let forceRequired = false
                        let isOverrideJustificationQuestion = false
                        if (relatedQuestionId) {
                          const relatedQuestionAnswer = indexedAnswers[relatedQuestionId]?.answer?.toString()
                          const matchesOverrideJustificationTitle = isOverrideJustificationTitle(question.title)
                          const matchesOverrideJustificationTarget =
                            relatedQuestionTargetOptions?.some(
                              option => option.toLowerCase() === '__not_suggested__'
                            ) ?? false
                          isOverrideJustificationQuestion =
                            talentRankQuestion?.id === relatedQuestionId &&
                            (matchesOverrideJustificationTitle || matchesOverrideJustificationTarget)
                          if (isOverrideJustificationQuestion) {
                            const suggestedValue = talentRankSummary?.suggestionValue
                            const hasSuggestion = suggestedValue !== undefined && relatedQuestionAnswer !== undefined
                            showQuestion = hasSuggestion ? relatedQuestionAnswer !== suggestedValue : false
                            forceRequired = showQuestion
                          } else if (relatedQuestionTargetOptions) {
                            showQuestion = relatedQuestionTargetOptions.includes(relatedQuestionAnswer as string)
                          }
                          if (!showQuestion && indexedAnswers[question.id]) {
                            indexedAnswers[question.id].answer = ''
                          }
                        }
                        if (showQuestion) {
                          const isRequired = question.required || forceRequired
                          return (
                            <TextareaQuestion
                              key={`employee-review-${currentTopic}-question-${i}`}
                              value={(indexedAnswers[question.id]?.answer as string) || ''}
                              title={question.title}
                              placeholder={question.description}
                              required={isRequired}
                              unfinished={isUnfinishedTopic}
                              onChange={handleQuestionAnswered(question.id)}
                            />
                          )
                        }
                        return null
                      case QuestionTypeEnum.TRUE_FALSE:
                        return (
                          <TrueFalseQuestion
                            title={question.title}
                            label={question.label}
                            value={(indexedAnswers[question.id]?.answer as boolean) || false}
                            required={question.required}
                            onChange={handleQuestionAnswered(question.id)}
                          ></TrueFalseQuestion>
                        )
                      default:
                        return <></>
                    }
                  })}
              </div>
              <div className="flex flex-col">
                <NextPrevArrows
                  onNext={isLastTopic ? handleFinishReview : handleGoToNextTopic}
                  onPrev={handleBackToPreviousTopic}
                  isNextDisabled={!areRequiredQuestionsAnswered ? isLastTopic : false}
                  isPrevDisabled={isFirstTopic}
                  {...arrowLabelProps}
                />
                {showBackToTop ? (
                  <div className="flex justify-center">
                    <button
                      className="flex justify-around items-center bg-orange-soft text-black text-xl px-3 py-2 w-[100px] rounded-[8px]"
                      onClick={handleGoTop}
                    >
                      <div className="rotate-90">
                        <ArrowIcon height={8.5} width={10.5} color={'#000000'} />
                      </div>
                      Back to top
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
      <OptionsModal
        visible={showChangeFormModal === true}
        message={
          <p>
            <span className={`modal-title`}>Are you sure you want to change the form?</span>
            <br />
            <b>Your current assessment will be lost</b>
          </p>
        }
        icon={<WarningIcon />}
        firstButtonText="No"
        firstButtonType="clear"
        secondButtonText="Yes"
        secondButtonType="info"
        onFirstButtonClick={cancelFormChange}
        onSecondButtonClick={() => confirmFormChange()}
        onClose={cancelFormChange}
      />
      <div>
        {showErrorModal && (
          <ErrorModal
            icon={<WarningIcon />}
            visible={showErrorModal}
            message={
              <p>
                <span className={`modal-title`}>Something went wrong while saving your answers.</span>
                <br />
                <span className="text-grey">
                  <b>Try reloading the page and save again.</b> If the problem persists,{' '}
                  <a href={EMAIL}>contact support</a>.
                </span>
              </p>
            }
            onClose={() => setShowErrorModal(false)}
          />
        )}
      </div>
      <OptionsModal
        icon={<DeleteIcon />}
        secondButtonText="Delete"
        secondButtonType="error"
        firstButtonText="Cancel"
        firstButtonType="clear"
        visible={showDeleteModal}
        onFirstButtonClick={handleCancelDelete}
        onSecondButtonClick={handleConfirmDelete}
        onClose={handleCancelDelete}
        message={deleteModalMessage}
      />
    </div>
  )
}
